import { Tag } from '@phosphor-icons/react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { DefinitionBox } from '../../components/onboarding/DefinitionBox';
import { SelectionCountBadge } from '../../components/onboarding/SelectionCountBadge';
import { SelectionGroup } from '../../components/onboarding/SelectionGroup';
import { SectionSkeleton } from '../../components/common/SectionSkeleton';
import { WizardErrorCallout, WizardStepHeader } from '../../components/wizard';
import { useOnboardingFooter } from '../../hooks/useOnboardingFooter';
import { authAPI } from '../../data/auth';
import { expensesAPI, normalizeCategoryList } from '../../data/expenses';
import {
  CATEGORY_GROUPS,
  ONBOARDING_CATEGORY_PRESELECT,
  ONBOARDING_DEFINITIONS,
} from '../../lib/onboardingCatalogs';
import pt from '../../locales/pt';
import { useAuth } from '../../store/AuthContext';

function normalizeName(value) {
  return String(value || '')
    .trim()
    .toLowerCase();
}

export function matchCategoryNamesFromExisting(catalogItems, existingCategories) {
  const existingNames = new Set(
    (existingCategories || []).map((item) => normalizeName(item.name)).filter(Boolean),
  );
  return (catalogItems || [])
    .map((item) => item.name)
    .filter((name) => existingNames.has(normalizeName(name)));
}

export function filterNewCategoryNames(selectedNames, existingCategories) {
  const existingNames = new Set(
    (existingCategories || []).map((item) => normalizeName(item.name)).filter(Boolean),
  );
  return (selectedNames || []).filter((name) => !existingNames.has(normalizeName(name)));
}

export function OnboardingCategorySelect() {
  const navigate = useNavigate();
  const { refreshMe } = useAuth();
  const [existingCategories, setExistingCategories] = useState([]);
  const [loadingCategories, setLoadingCategories] = useState(true);
  const [selected, setSelected] = useState([]);
  const [saving, setSaving] = useState(false);
  const [serverError, setServerError] = useState(null);
  const didHydrate = useRef(false);

  const allItems = useMemo(() => CATEGORY_GROUPS.flatMap((group) => group.items), []);
  const selectedSet = useMemo(() => new Set(selected), [selected]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const response = await expensesAPI.listCategories();
        if (cancelled) return;
        if (response?.success) {
          setExistingCategories(normalizeCategoryList(response.data));
        }
      } finally {
        if (!cancelled) setLoadingCategories(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (loadingCategories || didHydrate.current || allItems.length === 0) return;
    const matched = matchCategoryNamesFromExisting(allItems, existingCategories);
    if (matched.length > 0) {
      setSelected(matched);
    } else {
      const available = new Set(allItems.map((item) => item.name));
      setSelected(ONBOARDING_CATEGORY_PRESELECT.filter((name) => available.has(name)));
    }
    didHydrate.current = true;
  }, [allItems, existingCategories, loadingCategories]);

  const toggle = (name) => {
    setSelected((current) =>
      current.includes(name) ? current.filter((c) => c !== name) : [...current, name],
    );
  };

  const advance = useCallback(
    async (skipCreate = false) => {
      setSaving(true);
      setServerError(null);

      if (!skipCreate && selected.length > 0) {
        const newNames = filterNewCategoryNames(selected, existingCategories);
        if (newNames.length > 0) {
          const response = await expensesAPI.bulkCreateCategories(
            newNames.map((name) => ({ name })),
          );
          if (!response.success) {
            setServerError(
              typeof response.error === 'string'
                ? response.error
                : response.error?.detail || pt.auth.errors.generic,
            );
            setSaving(false);
            return;
          }
        }
      }

      await authAPI.saveOnboardingStep('accounts');
      await refreshMe();
      setSaving(false);
      navigate('/onboarding/accounts');
    },
    [existingCategories, navigate, refreshMe, selected],
  );

  const onContinue = useCallback(() => advance(false), [advance]);
  const onSkip = useCallback(() => advance(true), [advance]);

  useOnboardingFooter({
    primaryLabel: pt.onboarding.common.continue,
    onPrimary: onContinue,
    onSkip,
    skipLabel: pt.onboarding.common.skipLater,
    loading: saving,
  });

  if (loadingCategories) {
    return <SectionSkeleton variant="list" />;
  }

  return (
    <div className="flex flex-col gap-space-6">
      <div className="flex items-start justify-between gap-3">
        <WizardStepHeader
          eyebrow={pt.onboarding.categories.eyebrow}
          title={pt.onboarding.categories.title}
          subtitle={pt.onboarding.categories.subtitle}
          className="flex-1"
        />
        <SelectionCountBadge count={selected.length} className="mt-7 shrink-0" />
      </div>

      <DefinitionBox icon={Tag}>{ONBOARDING_DEFINITIONS.categories}</DefinitionBox>

      {CATEGORY_GROUPS.map((group) => (
        <SelectionGroup
          key={group.id}
          name={group.name}
          pctLabel={group.pctLabel}
          description={group.description}
          dotClass={group.dotClass}
          tint={group.tint}
          items={group.items}
          selectedKeys={selectedSet}
          getKey={(item) => item.name}
          onToggle={(name) => toggle(name)}
        />
      ))}

      {serverError ? <WizardErrorCallout>{serverError}</WizardErrorCallout> : null}
    </div>
  );
}

export const CATEGORY_SUGGESTIONS = CATEGORY_GROUPS.flatMap((g) =>
  g.items.map((item) => ({ name: item.name, icon: '•' })),
);
